import React, { useMemo, useState } from 'react';
import cn from 'classnames';

import { beGetSalonTaxesAndFees } from 'actions/salon/beGetSalonTaxesAndFees.action';
import TypedOption from 'components/form/TypedOption.component';
import Space from 'components/generic/Space.component';
import { TaxesAndFeesSection } from 'components/taxes/TaxesAndFeesSection/TaxesAndFeesSection.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import PERM from 'constants/core/perm.const';
import IN from 'constants/in';
import { NO } from 'constants/no';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import logger from 'utils/logger.util';

import DropdownContainer from 'onboarding/common/forms/common/Popup';

import { getServiceTaxesAndFees } from './actions';
import RoleCheckboxContainer from './RoleCheckbox';

import CN from './AddServiceWithRole.module.scss';

const L = logger('AddServiceWithRole');

export const AddServiceWithRole = ({
  selectedCategory: selectedCategoryFromProps,
  onCategoryChange,
  onCheckboxChange,
  onPriceBlur,
  onApplicationChange,
  onProcessingChange,
  onPrincingChange,
  onCategoryChangeInSettings,
  settingsLayout,
  onPriceChange,
  quickSetupLayout,
  service,
  serviceId,
  roles,
  categories,
  onSelectTax,
  onSelectFee,
  onToggleExempt,
  onChangeReason,
}) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [clicked, setClicked] = useState(false);
  const [selectPopupModal, setSelectPopupModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(selectedCategoryFromProps);

  const { qj: taxesAndFees, isLoading } = usePrepareReadOnly(beGetSalonTaxesAndFees);

  const taxes = useMemo(() => taxesAndFees?.taxes ?? [], [taxesAndFees]);
  const fees = useMemo(() => taxesAndFees?.fees ?? [], [taxesAndFees]);

  const isEdit = service?.id || service?.service_id;
  const {
    qr: { isFetching: isFetchingServiceTaxesAndFees },
  } = usePrepareReadOnly(getServiceTaxesAndFees, { serviceId, refetchOnMount: true, enabled: isEdit });

  const servicesTaxes = taxes.filter((tax) => tax.applied_to === 'services' || tax.applied_to === 'products_and_services');
  const servicesFees = fees.filter((fee) => fee.applied_to === 'services' || fee.applied_to === 'products_and_services');

  const editedTaxes = useMemo(() => service?.tax_ids ?? [], [service]);
  const editedFees = useMemo(() => service?.fee_ids ?? [], [service]);

  const defaultTaxes = isEdit
    ? {
        ...editedTaxes?.reduce((acc, tax) => {
          acc[tax] = true;
          return acc;
        }, {}),
      }
    : undefined;

  const defaultFees = isEdit
    ? {
        ...editedFees?.reduce((acc, fee) => {
          acc[fee] = true;
          return acc;
        }, {}),
      }
    : undefined;

  const onCategoryClick = () => {
    setSelectPopupModal(true);
  };

  const onCheckboxClick = (roleId) => {
    if (!clicked) {
      setClicked(true);
    }
    onCheckboxChange(roleId);
  };

  const changePricing = () => {
    if (!quickSetupLayout) {
      onPrincingChange(!service.variable_pricing);
    }
  };

  const onPopupClose = () => {
    setSelectPopupModal(false);
  };

  const onSelectItem = (item) => {
    for (let i = 0; i < categories.length; i += 1) {
      const currentCategory = categories[i];
      if (item.id === currentCategory.id) {
        setSelectedCategory(currentCategory);
        setSelectPopupModal(false);
        if (settingsLayout) {
          onCategoryChangeInSettings && onCategoryChangeInSettings(service, currentCategory);
        } else {
          onCategoryChange(currentCategory);
        }
        break;
      }
    }
  };

  const selectedCategoryId = selectedCategory && selectedCategory.id;

  const items = categories.map(({ id, name }) => ({
    id: id || NO.id,
    name,
    active: !!(selectedCategoryId && selectedCategoryId === id),
  }));

  const componentClasses = cn({
    [CN.component]: true,
    [CN.darkMode]: isDarkMode,
  });

  L.debug('render()', serviceId, service, { selectedCategoryId, roles });

  return (
    <div data-bem="AddServiceWithRole" className={componentClasses}>
      {selectPopupModal && categories.length && <DropdownContainer title={t('composite.addService.dropdownTitle')} items={items} onClose={onPopupClose} onSelectItem={onSelectItem} />}
      {categories.length > 0 && (
        <div>
          <label htmlFor="ServiceTypeSelect">
            {t('composite.addService.serviceCategory')}
            <button className="utl-full-width select-button" id="ServiceTypeSelect" onClick={onCategoryClick}>
              <span className="select-button-bullet" style={{ backgroundColor: selectedCategory.color }} />
              <span className={CN.dropdownName}>{selectedCategory.name}</span>
              <span className="select-button-icon">
                <span className="ic-sml ic_expand_more_black" />
              </span>
            </button>
          </label>
          <div className="input-baseline" />
          <div className="input-prompt-hide">
            <div className="input-baseline" />
            <span>{t('composite.addService.errorMessage')}</span>
          </div>
        </div>
      )}
      <div data-bem="AddServiceWithRole__roles-container">
        <label style={{ marginTop: 25, marginBottom: 10 }} htmlFor="roles">
          {!quickSetupLayout && <span className="input-label">{t('composite.addService.selectStylistLabel')}</span>}
          <div data-bem="AddServiceWithRole__roles-list" id="roles">
            {roles?.map((role) => {
              const roleId = role.id;
              const key = `${roleId}-${serviceId || `unknown`}`;
              return (
                <div data-bem={`AddServiceWithRole__role--${key}`} key={key} className="relative">
                  <RoleCheckboxContainer
                    role={role}
                    readOnly={quickSetupLayout}
                    service={service}
                    onClick={onCheckboxClick}
                    onPriceChange={onPriceChange}
                    onPriceBlur={onPriceBlur}
                    onApplicationChange={onApplicationChange}
                    onProcessingChange={onProcessingChange}
                  />
                  <TooltipForReceptionist permission={PERM.canEditServices} where={IN.role} />
                </div>
              );
            })}
          </div>
        </label>
      </div>
      <div className={CN.varPricingContainer}>
        <TypedOption tag="ClientEmails__client-select-all" type="tooltip-radio" checked={service && service.variable_pricing} onCheck={changePricing}>
          {t('composite.addService.checkboxLabel')}
          <Space bold>{t('composite.addService.priceStartingFrom')}</Space>
        </TypedOption>
      </div>
      <TaxesAndFeesSection
        isEdit={isEdit}
        location="service"
        taxes={servicesTaxes}
        fees={servicesFees}
        isLoading={isLoading || isFetchingServiceTaxesAndFees}
        onSelectTax={onSelectTax}
        onSelectFee={onSelectFee}
        onToggleExempt={onToggleExempt}
        onChangeReason={onChangeReason}
        taxesByDefault={isEdit ? defaultTaxes : undefined}
        feesByDefault={isEdit ? defaultFees : undefined}
        isExemptByDefault={isEdit ? service?.is_tax_exempt : undefined}
        reasonByDefault={isEdit ? service?.tax_exempt_reason : undefined}
      />
    </div>
  );
};
